import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { useQuery } from '../../../request/hooks/useQuery';
import { GenericApiResponse } from '../../interfaces/generics';
import { EntityUIState } from '../../../request/interfaces/entity-ui-state';
import { RetryApiAlert } from '../RetryApiAlert/RetryApiAlert';
import { getErrorMessageFromAxiosError } from '../../../request/utils';
import { Banner } from '../../../banner/Banner';
import { LinkButton } from '../../../link/LinkButton';
import { AffectedEntityListShimmer } from './components/AffectedEntityListShimmer';
import { Integration, RawIntegration } from '../../interfaces/integration';
import { useScrollToBottom } from '../../hooks/useScrollToBottom';
import { Source } from '../../interfaces/source';
import { Destination } from '../../interfaces/destination';

export interface AffectedEntityListViewProps {
  entity: Integration | Source | Destination;
  entityActionResolver: (id: number) => Promise<GenericApiResponse<RawIntegration[]>>;
  entityFactory: (data: RawIntegration[]) => Integration[];
  affectedEntity: string;
  affectingEntity: string;
  setAffectedEntityName: React.Dispatch<React.SetStateAction<string>>;
}

export function AffectedEntityListView({
  entity,
  entityActionResolver,
  entityFactory,
  affectedEntity,
  affectingEntity,
  setAffectedEntityName
}: AffectedEntityListViewProps) {
  const [showAll, setShowAll] = useState(false);
  const [affectedPipelines, setAffectedPipelines] = useState<Integration[]>([]);
  const listContainerRef = useRef<HTMLOListElement>(null);

  const { scrollToBottom } = useScrollToBottom(listContainerRef);

  const initialDisplayCount = 3;

  const {
    data: affectedEntityData,
    uiState,
    error: apiError,
    fetch: fetchAffectedEntity
  } = useQuery({
    dataSource$: () => entityActionResolver(entity.id).then(res => entityFactory(res.data))
  });

  useEffect(() => {
    fetchAffectedEntity();
  }, []);

  useEffect(() => {
    if (affectedEntityData && showAll) {
      setAffectedPipelines(affectedEntityData);
    } else if (affectedEntityData && !showAll) {
      setAffectedPipelines(
        affectedEntityData.length > 3 ? affectedEntityData.slice(0, 3) : affectedEntityData
      );
    }

    if (setAffectedEntityName) {
      setAffectedEntityName(uiState === EntityUIState.ERRORED ? '' : (entity?.name as string));
    }
  }, [affectedEntityData, showAll, uiState]);

  useLayoutEffect(() => {
    if (showAll) {
      scrollToBottom();
    }
  }, [affectedPipelines]);

  const remainingCount = affectedEntityData ? affectedEntityData.length - initialDisplayCount : 0;

  const toggleView = () => setShowAll(!showAll);

  const renderContent = () => {
    switch (uiState) {
      case EntityUIState.LOADING:
        return <AffectedEntityListShimmer className='w-100' />;
      case EntityUIState.ERRORED:
        return (
          <div className='w-100 h-100 center-flex-row justify-center'>
            <RetryApiAlert
              title='Something went wrong. Please try again.'
              subTitle={getErrorMessageFromAxiosError(apiError as AxiosError)}
              secondaryCTAProps={{ onClick: fetchAffectedEntity }}
              compact
              dataId='retryAlert'
              className='p-2 flex-row'
            />
          </div>
        );
      default:
        return (
          <>
            <ol ref={listContainerRef} className='scrollable-content' style={{ maxHeight: '50px' }}>
              {affectedPipelines.map((integration: Integration) => (
                <li key={integration.id} data-id={`affected-entity-${integration.id}`}>
                  <span className='text-bold'>{integration.name}</span>
                </li>
              ))}
            </ol>

            {!showAll && affectedEntityData && affectedEntityData.length > initialDisplayCount && (
              <p className='text-body-1 text-secondary ml-3'>
                ... {remainingCount} more {affectedEntity}
              </p>
            )}

            {affectedEntityData && affectedEntityData.length > initialDisplayCount && (
              <LinkButton className='ml-3' onClick={toggleView} dataId='viewAllButton'>
                {showAll ? 'See Less' : 'View All'}
              </LinkButton>
            )}
          </>
        );
    }
  };

  return (
    <>
      {uiState !== EntityUIState.LOADING &&
      uiState !== EntityUIState.ERRORED &&
      affectedPipelines &&
      affectedPipelines.length === 0 ? (
        <Banner className='mb-3' open dataId='noEntityInfoBanner' color='primary' icon='info'>
          <span className='text-body-1'>{`The are no ${affectedEntity} connected to this ${affectingEntity}`}</span>
        </Banner>
      ) : (
        <>
          <Banner className='mb-5' open dataId='errorBanner' color='error' icon='error-outline'>
            <span className='text-body-1'>{`All the ${affectedEntity} connected to this ${affectingEntity} will be deleted as well`}</span>
          </Banner>

          <span className='text-body-1 text-secondary text-center'>
            {`The following ${affectedEntity} will get deleted:`}
          </span>
        </>
      )}

      {renderContent()}
    </>
  );
}
